/** Lazy, shared read-only Word and PDF surfaces for every authorized document preview. */
import { createRoot, type Root } from 'react-dom/client';
const selector =
  '[data-document-word-preview], [data-document-pdf-preview], [data-file-comments-list]';
const mounts = new Map<HTMLElement, Root | null>();
async function mount(host: HTMLElement) {
  if (mounts.has(host)) return;
  mounts.set(host, null);
  try {
    if (host.hasAttribute('data-file-comments-list')) {
      const { FileComments } = await import('./FileComments');
      if (!host.isConnected) {
        mounts.delete(host);
        return;
      }
      const root = createRoot(host);
      mounts.set(host, root);
      root.render(<FileComments host={host} />);
      return;
    }
    const isPdf = host.hasAttribute('data-document-pdf-preview');
    const Component = isPdf
      ? (await import('./PdfPreview')).default
      : (await import('./WordPreview')).default;
    if (!host.isConnected) {
      mounts.delete(host);
      return;
    }
    const root = createRoot(host);
    mounts.set(host, root);
    root.render(
      <Component
        url={host.dataset.pdfUrl || host.dataset.docxUrl || ''}
        title={host.dataset.filename || 'PDF'}
        host={host}
      />,
    );
  } catch {
    mounts.delete(host);
    const fallback = host.parentElement?.querySelector<HTMLElement>(
      '[data-word-fallback]',
    );
    if (fallback) {
      fallback.hidden = false;
      host.hidden = true;
    } else
      host.textContent =
        'Preview unavailable. Download the original file to open it.';
  }
}
function scan(node: ParentNode) {
  node
    .querySelectorAll<HTMLElement>(selector)
    .forEach((host) => void mount(host));
}
scan(document);
new MutationObserver((records) => {
  for (const record of records)
    for (const node of record.addedNodes)
      if (node instanceof HTMLElement) {
        if (node.matches(selector)) void mount(node);
        else if (node.querySelector(selector)) scan(node);
      }
  for (const [host, root] of mounts)
    if (!host.isConnected) {
      root?.unmount();
      mounts.delete(host);
    }
}).observe(document.body, { childList: true, subtree: true });
